import React from 'react';
import { Pill } from '../../../shared/Pill';
import { formatDate } from '../../../shared/tenantClock';
import { businessNumberLabel } from '../../../shared/businessNumber';

const LABEL_STYLE = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3 };
const ROW_LABEL_STYLE = { color: '#5a6b85', paddingRight: 16, whiteSpace: 'nowrap', verticalAlign: 'top' };
const FLAG_STYLE = { color: '#b3261e', fontSize: 14, marginTop: 6 };

// What the register (register.label, e.g. ABN Lookup) last said about the
// business's Business Number (entity.business_number_check, kept by
// backend/services/businessNumberCheckService.js), on the Legal card. Flags
// a number that isn't active or wasn't found, a Legal Entity that differs
// from the register's entity type, and a primary address in another state
// or postcode.
function BusinessNumberCheckDetails({ entity, register, legalEntityTypes }) {
  const check = entity.business_number_check;
  const current = check && check.number === entity.business_number ? check : null;
  const numberLabel = businessNumberLabel(register.numberType);

  let body;
  if (!current) {
    body = <div style={{ color: '#5a6b85', fontSize: 14 }}>Not checked yet. Use Check {numberLabel} in this card&apos;s menu.</div>;
  } else if (current.status === 'unavailable') {
    body = <div style={{ color: '#5a6b85', fontSize: 14 }}>Couldn&apos;t reach {register.label}. Use Check {numberLabel} to try again.</div>;
  } else if (current.status === 'not_found') {
    body = <div style={FLAG_STYLE}>{numberLabel} not found on {register.label}.</div>;
  } else {
    const active = /^active$/i.test(current.number_status || '');
    const lookupType = legalEntityTypes.find((t) => String(t.id) === String(current.legal_entity_type_id));
    const flags = [];
    if (!active) flags.push(`${numberLabel} is ${current.number_status || 'not active'}.`);
    if (entity.legal_entity_type_id && current.legal_entity_type_id && String(entity.legal_entity_type_id) !== String(current.legal_entity_type_id)) {
      flags.push(`${register.label} says ${current.entity_type_name}${lookupType ? ` (${lookupType.name})` : ''}; Legal Entity is ${entity.legal_entity_type?.name}.`);
    }
    const address = entity.primary_address;
    if (address?.country === 'Australia') {
      if (current.state && address.state && current.state !== address.state) flags.push(`State differs: ${register.label} has ${current.state}, the primary address ${address.state}.`);
      if (current.postcode && address.postcode && current.postcode !== address.postcode) flags.push(`Postcode differs: ${register.label} has ${current.postcode}, the primary address ${address.postcode}.`);
    }
    const rows = [
      ['Registered name', current.name],
      [`${numberLabel} status`, active ? current.number_status : <Pill color="#b3261e" background="#fde7e7">{current.number_status || 'Not active'}</Pill>],
      ['GST', current.gst_registered ? `Registered${current.gst_from ? ` from ${formatDate(current.gst_from)}` : ''}` : 'Not registered'],
      ['Entity type', current.entity_type_name || '—'],
      ['State', current.state || '—'],
      ['Postcode', current.postcode || '—'],
    ];
    body = (
      <>
        <table style={{ borderCollapse: 'collapse', fontSize: 14 }}>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td style={ROW_LABEL_STYLE}>{label}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {flags.map((flag) => <div key={flag} style={FLAG_STYLE}>{flag}</div>)}
      </>
    );
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={LABEL_STYLE}>
        {register.label}{current?.checked_at && <span style={{ textTransform: 'none', letterSpacing: 0 }}> · checked {formatDate(current.checked_at)}</span>}
      </div>
      {body}
    </div>
  );
}

export default BusinessNumberCheckDetails;
